import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const ACCESS_LEVELS = ['free', 'taster', 'collector', 'enthusiast', 'connoisseur', 'vip'];
const PAGE_SIZE = 50;

const TIER_BADGE = {
  free: 'bg-gray-100 text-gray-700',
  taster: 'bg-blue-50 text-blue-700',
  collector: 'bg-purple-50 text-purple-700',
  enthusiast: 'bg-pink-50 text-pink-700',
  connoisseur: 'bg-amber-50 text-amber-700',
  vip: 'bg-red-50 text-red-700',
};

function TierBadge({ tier }) {
  if (!tier) return null;
  const cls = TIER_BADGE[tier] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${cls}`}>
      {tier}
    </span>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">{value}</dd>
        {sub && <div className="mt-2 text-sm text-gray-600">{sub}</div>}
      </div>
    </div>
  );
}

const SORT_COLUMNS = [
  { key: 'bottles', label: 'Bottles', defaultDir: 'desc' },
  { key: 'uniqueWines', label: 'Unique', defaultDir: 'desc' },
  { key: 'locations', label: 'Locations', defaultDir: 'desc' },
  { key: 'lastAdded', label: 'Last added', defaultDir: 'desc' },
  { key: 'name', label: 'Name', defaultDir: 'asc' },
];

export default function UserLibrary() {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tier, setTier] = useState('');
  const [minBottles, setMinBottles] = useState(1);
  const [active30, setActive30] = useState(false);
  const [sort, setSort] = useState('bottles');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = {
        $limit: PAGE_SIZE,
        $skip: page * PAGE_SIZE,
        $minBottles: minBottles,
        $sort: sort,
        $sortDir: sortDir,
      };
      if (tier) query.$tier = tier;
      if (active30) query.$active30 = true;

      const result = await client.service('admin/user-library-report').find({ query });
      setReport(result);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [tier, minBottles, active30, sort, sortDir, page]);

  useEffect(() => { load(); }, [load]);

  // Whenever filter changes, jump back to page 0.
  useEffect(() => { setPage(0); }, [tier, minBottles, active30, sort, sortDir]);

  const handleSortClick = (key) => {
    if (sort === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      const col = SORT_COLUMNS.find((c) => c.key === key);
      setSortDir(col?.defaultDir || 'desc');
    }
  };

  const total = report?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const summary = report?.summary;
  const avgBottles = useMemo(() => {
    if (!summary || !summary.users_with_cellar) return null;
    return Math.round(summary.total_bottles / summary.users_with_cellar);
  }, [summary]);

  return (
    <Page title="User Libraries">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: `/admin/reports` },
            { name: 'User Libraries' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">{error}</div>
        )}

        {!report && !error && <p>Loading…</p>}

        {summary && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <Kpi label="Users with a cellar" value={summary.users_with_cellar} />
              <Kpi label="Total bottles" value={summary.total_bottles} sub={avgBottles ? `${avgBottles} avg / user` : null} />
              <Kpi label="Median cellar size" value={summary.median_bottles} sub="bottles" />
              <Kpi label="Active in 30 days" value={summary.active_30d} sub="users who added a bottle" />
            </div>

            <div className="bg-white shadow rounded-lg p-4 mb-4 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Tier
                <select
                  className="rounded border-gray-300 text-sm py-1"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                >
                  <option value="">All</option>
                  {ACCESS_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Min bottles
                <input
                  type="number"
                  min={0}
                  className="rounded border-gray-300 text-sm py-1 w-20"
                  value={minBottles}
                  onChange={(e) => setMinBottles(Math.max(0, parseInt(e.target.value, 10) || 0))}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={active30}
                  onChange={(e) => setActive30(e.target.checked)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                Active in last 30 days
              </label>
              <div className="ml-auto text-sm text-gray-500">
                {loading ? 'Loading…' : `${total} user${total !== 1 ? 's' : ''} match`}
              </div>
            </div>

            <div className="bg-white shadow rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSortClick('name')}
                    >
                      User
                      {sort === 'name' && (
                        <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                    {SORT_COLUMNS.filter((c) => c.key !== 'name').map((c) => (
                      <th
                        key={c.key}
                        className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSortClick(c.key)}
                      >
                        {c.label}
                        {sort === c.key && (
                          <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Past peak</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.data.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500">
                        No users match these filters.
                      </td>
                    </tr>
                  )}
                  {report.data.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">
                        <Link to={`/admin/users/${u.id}`} className="text-red-600 hover:text-red-900 font-medium">
                          {u.firstName} {u.lastName}
                        </Link>
                        <div className="text-gray-500 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <TierBadge tier={u.accessLevel} />
                          {u.hasOverride && (
                            <span title="Active comp / override" className="text-indigo-600 text-xs">★</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-sm text-gray-900 font-medium">{u.totalBottles}</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-600">{u.uniqueWines}</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-600">{u.locationCount}</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-500">
                        {u.lastAdded
                          ? formatDistance(new Date(u.lastAdded), new Date(), { addSuffix: true })
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-sm">
                        {u.pastPeakCount > 0 ? (
                          <span className="text-red-700 font-medium">{u.pastPeakCount}</span>
                        ) : (
                          <span className="text-gray-300">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
